/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

/**
 * Condominium prefill (plan §4.4, rule 6): choosing an existing condominium
 * suggests what the agency's other listings in it share. Only fields that
 * are still empty are filled; what the user already typed is never touched.
 */

import type { CondominiumPrefillResponse } from '@services/requests/generated/types.gen';
import type { PropertyFormValues } from './schema';

type FormKey = keyof PropertyFormValues;

/** Where each suggested value goes in the form: [block, key, form field, notice group]. */
const PREFILL_FIELDS: ReadonlyArray<readonly ['location' | 'condominium', string, FormKey, string]> = [
  ['location', 'street', 'street', 'address'],
  ['location', 'number', 'number', 'address'],
  ['location', 'postal_code', 'postal_code', 'postalCode'],
  ['location', 'neighborhood', 'neighborhood', 'neighborhood'],
  ['location', 'city', 'city', 'address'],
  ['location', 'state', 'state', 'address'],
  ['condominium', 'developer', 'developer', 'developer'],
  ['condominium', 'development_name', 'development_name', 'development'],
  ['condominium', 'monthly_fee', 'condominium_price', 'condoFee'],
  ['condominium', 'leisure_areas', 'condo_amenities', 'condoLeisure'],
  ['condominium', 'security', 'condo_security', 'condoSecurity'],
  ['condominium', 'included_expenses', 'condo_included_expenses', 'condoExpenses'],
];

/** The result of matching a prefill against the form. */
export interface CondominiumPrefillPlan {
  /** Form fields to set, all of them empty in the form today. */
  patch: Partial<Record<FormKey, unknown>>;
  /** Notice groups that received a value, in display order, without repeats. */
  groups: string[];
}

/** How each JavaScript type tells it holds nothing. */
const BLANK_BY_TYPE: Partial<Record<string, (value: unknown) => boolean>> = {
  string: (value) => (value as string).trim() === '',
  number: (value) => value === 0 || Number.isNaN(value),
  object: (value) => Array.isArray(value) && value.length === 0,
};

/**
 * Whether a value holds nothing (the form treats it as not filled).
 *
 * @param value - A form or suggested value.
 * @returns True for nullish, blank text, zero, NaN and empty lists.
 */
export function isBlankValue(value: unknown): boolean {
  if (value == null) return true;
  const check = BLANK_BY_TYPE[typeof value];
  return check ? check(value) : false;
}

/**
 * Whether a suggestion should go into the field: it has a value and the
 * field has none (rule 6: never overwrite what the user typed).
 *
 * @param suggested - The shared value.
 * @param current - The field's value now.
 * @returns True when the field is to be filled.
 */
function fillsEmptyField(suggested: unknown, current: unknown): boolean {
  return !isBlankValue(suggested) && isBlankValue(current);
}

/**
 * Plans which empty form fields the shared condominium values fill.
 *
 * @param current - The form values right now.
 * @param values - The `values` of the prefill response.
 * @returns The fields to set and the notice groups they belong to.
 */
export function planCondominiumPrefill(
  current: Partial<PropertyFormValues>,
  values: CondominiumPrefillResponse['values'] | undefined,
): CondominiumPrefillPlan {
  const patch: CondominiumPrefillPlan['patch'] = {};
  const groups: string[] = [];
  for (const [block, key, formKey, group] of PREFILL_FIELDS) {
    const suggested = values?.[block]?.[key];
    if (!fillsEmptyField(suggested, current[formKey])) continue;
    patch[formKey] = suggested;
    groups.push(group);
  }
  return { patch, groups: [...new Set(groups)] };
}
